import React, { useState, useEffect, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { supabase } from "../lib/supabase";
import toast from "react-hot-toast";

export default function GearTable({
	gearList,
	setGearList,
	gearTypes,
	gearStatuses,
	userRole,
	user,
	addGear,
	updateGearField,
	removeGear,
	selectedGear,
	setSelectedGear,
	allCases,
	caseName,
	associatedKits,
}) {
	const [editingCell, setEditingCell] = useState(null);
	const [newGroupName, setNewGroupName] = useState("");
	const [newGearByGroup, setNewGearByGroup] = useState({});
	const [editingGroup, setEditingGroup] = useState(null);
	const [renamingGroups, setRenamingGroups] = useState({});
	const [showMoveModal, setShowMoveModal] = useState(false);
	const [showQuantityModal, setShowQuantityModal] = useState(false);
	const [tempQuantity, setTempQuantity] = useState("");
	const [showStatusModal, setShowStatusModal] = useState(false);
	const [tempStatusId, setTempStatusId] = useState("");
	const [moveOptions, setMoveOptions] = useState([]);
	const [expandedKits, setExpandedKits] = useState([]);

	const modalRef = useRef(null);

	const popupRef = useRef(null);

	useEffect(() => {
		function handleClickOutside(event) {
			if (popupRef.current && !popupRef.current.contains(event.target)) {
				setEditingCell(null);
			}
			if (modalRef.current && !modalRef.current.contains(event.target)) {
				setShowMoveModal(false);
			}
		}
		document.addEventListener("mousedown", handleClickOutside);
		return () =>
			document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	useEffect(() => {
		const fetchGroupedCases = async () => {
			try {
				const [{ data: cases }, { data: kits }, { data: kitCases }] =
					await Promise.all([
						supabase.from("cases").select("id, name"),
						supabase.from("kits").select("id, name"),
						supabase.from("kit_cases").select("kit_id, case_id"),
					]);

				const grouped = [];

				// Map kits by ID for lookup
				const kitMap = kits.reduce((acc, kit) => {
					acc[kit.id] = kit.name;
					return acc;
				}, {});

				// Build a map of kit_id => case[]
				const kitToCases = {};
				kitCases.forEach(({ kit_id, case_id }) => {
					if (!kitToCases[kit_id]) kitToCases[kit_id] = new Set();
					kitToCases[kit_id].add(case_id);
				});

				// Track which cases have been grouped
				const groupedCaseIds = new Set();

				// Build grouped entries
				for (const kitId in kitToCases) {
					const caseIds = kitToCases[kitId];
					const groupCases = cases.filter((c) => caseIds.has(c.id));
					grouped.push({
						groupLabel: `Kit: ${kitMap[kitId]}`,
						cases: groupCases,
					});
					groupCases.forEach((c) => groupedCaseIds.add(c.id));
				}

				// Find ungrouped cases
				const ungrouped = cases.filter(
					(c) => !groupedCaseIds.has(c.id)
				);
				if (ungrouped.length > 0) {
					grouped.unshift({
						groupLabel: "Singles",
						cases: ungrouped,
					});
				}
			} catch (err) {
				console.error("Failed to fetch grouped case list:", err);
			}
		};

		fetchGroupedCases();
	}, []);

	const toggleKit = (kitId) => {
		setExpandedKits((prev) =>
			prev.includes(kitId)
				? prev.filter((id) => id !== kitId)
				: [...prev, kitId]
		);
	};

	useEffect(() => {
		const fetchMoveOptions = async () => {
			try {
				const [
					{ data: allCases, error: allCasesError },
					{ data: kitsData, error: kitsError },
					{ data: groupData, error: groupError },
				] = await Promise.all([
					supabase.from("cases").select("id, name"),
					supabase
						.from("kits")
						.select("id, name, kit_cases ( cases ( id, name ) )"),
					supabase
						.from("gear")
						.select("case_id, group_name")
						.eq("name", "__group_placeholder__")
						.not("group_name", "is", null),
				]);

				if (allCasesError || kitsError || groupError) {
					console.error("Failed to fetch move options", {
						allCasesError,
						kitsError,
						groupError,
					});
					return;
				}

				const groupedCaseIds = new Set();

				const options = kitsData
					.map((kit) => {
						const cases = (kit.kit_cases || [])
							.map((kc) => {
								const caseItem = kc.cases;
								if (!caseItem) return null;

								groupedCaseIds.add(caseItem.id);

								const groupsForCase = groupData
									.filter((g) => g.case_id === caseItem.id)
									.map((g) => g.group_name);

								return {
									id: caseItem.id,
									name: caseItem.name,
									groups: groupsForCase,
								};
							})
							.filter(Boolean)
							.sort((a, b) => a.name.localeCompare(b.name));

						return {
							id: kit.id,
							name: kit.name,
							cases,
						};
					})
					.sort((a, b) => a.name.localeCompare(b.name));

				// Find ungrouped cases
				const ungroupedCases = allCases
					.filter((c) => !groupedCaseIds.has(c.id))
					.map((c) => {
						const groupsForCase = groupData
							.filter((g) => g.case_id === c.id)
							.map((g) => g.group_name);

						return {
							id: c.id,
							name: c.name,
							groups: groupsForCase,
						};
					})
					.sort((a, b) => a.name.localeCompare(b.name));

				if (ungroupedCases.length > 0) {
					options.unshift({
						id: "ungrouped",
						name: "Singles",
						cases: ungroupedCases,
					});
				}

				setMoveOptions(options);
			} catch (err) {
				console.error("Unexpected error fetching move options:", err);
			}
		};

		fetchMoveOptions();
	}, []);

	// --- 🧠 Core Logic: Grouping ---
	const ungroupedGear = gearList
		.filter((item) => !item.group_name)
		.sort((a, b) => a.order - b.order);
	const groupNames = Array.from(
		new Set([
			...gearList
				.map((item) => item.group_name)
				.filter((g) => g && g !== "__group_placeholder__"),
			...Object.keys(newGearByGroup).filter((g) => g !== "Singles"),
		])
	);

	const groupedGear = groupNames.map((groupName) => ({
		groupName,
		items: gearList
			.filter(
				(item) =>
					item.group_name === groupName &&
					item.name !== "__group_placeholder__"
			)
			.sort((a, b) => a.order - b.order), // <— sort by order here
	}));

	const toggleSelect = (gearId) => {
		setSelectedGear((prev) =>
			prev.includes(gearId)
				? prev.filter((id) => id !== gearId)
				: [...prev, gearId]
		);
	};

	const selectAllInGroup = (gearItems) => {
		const allSelected = gearItems.every((g) => selectedGear.includes(g.id));
		if (allSelected) {
			setSelectedGear((prev) =>
				prev.filter((id) => !gearItems.some((g) => g.id === id))
			);
		} else {
			setSelectedGear((prev) => [
				...new Set([...prev, ...gearItems.map((g) => g.id)]),
			]);
		}
	};

	const renderCheckboxCell = (gear) => (
		<div className="gear-table__cell checkbox-cell">
			<label className="custom-checkbox">
				<input
					type="checkbox"
					checked={selectedGear.includes(gear.id)}
					onChange={() => toggleSelect(gear.id)}
				/>
				<span className="checkmark">
					<i className="pi pi-check" />
				</span>
			</label>
		</div>
	);

	const renderSelectAllCheckbox = (gearItems) => {
		const allSelected =
			gearItems.length > 0 &&
			gearItems.every((g) => selectedGear.includes(g.id));

		return (
			<div className="gear-table__cell checkbox-cell">
				<label className="custom-checkbox">
					<input
						type="checkbox"
						checked={allSelected}
						onChange={() => selectAllInGroup(gearItems)}
						disabled={gearItems.length === 0}
					/>
					<span className="checkmark">
						<i className="pi pi-check" />
					</span>
				</label>
			</div>
		);
	};

	// --- 💾 Event Handlers ---
	async function handleInlineSave(gearId, field, value) {
		try {
			await updateGearField(gearId, field, value);
			setGearList((list) =>
				list.map((g) =>
					g.id === gearId ? { ...g, [field]: value } : g
				)
			);
		} catch (err) {
			alert("Failed to update: " + err.message);
		} finally {
			setEditingCell(null);
		}
	}

	async function handleAddNewGear(e, groupName) {
		if (e.key === "Enter" && newGearByGroup[groupName]?.trim()) {
			e.preventDefault();
			try {
				const newGear = await addGear({
					name: newGearByGroup[groupName].trim(),
					group_name: groupName !== "Singles" ? groupName : null,
				});
				setGearList((list) => [...list, newGear]);
				setNewGearByGroup((prev) => ({ ...prev, [groupName]: "" }));
			} catch (err) {
				alert("Failed to add gear: " + err.message);
			}
		}
	}

	async function handleAddNewGroup(e) {
		if (e.key === "Enter" && newGroupName.trim()) {
			e.preventDefault();
			const groupName = newGroupName.trim();
			const exists = gearList.some((g) => g.group_name === groupName);
			if (exists) {
				alert("Group already exists.");
				setNewGroupName("");
				return;
			}
			try {
				const newGroup = await addGear({
					name: "__group_placeholder__",
					group_name: groupName,
				});
				setGearList((list) => [...list, newGroup]);
				setNewGroupName("");
				setNewGearByGroup((prev) => ({
					...prev,
					[groupName]: "",
				}));
			} catch (err) {
				alert("Failed to add group: " + err.message);
			}
		}
	}

	function renderEditableCell(gear, field, type = "text", className = "") {
		const isEditing =
			editingCell?.gearId === gear.id && editingCell?.field === field;
		const value = gear[field] || "";
		const isAdmin = userRole === "admin";

		if (!isAdmin && field !== "notes") {
			return (
				<div className={`gear-table__cell ${className}`}>
					<div style={{ whiteSpace: "pre-wrap" }}>{value}</div>
				</div>
			);
		}

		if (isEditing) {
			return (
				<div className={`gear-table__cell ${className}`}>
					{type === "textarea" ? (
						<textarea
							defaultValue={value}
							autoFocus
							onBlur={(e) =>
								handleInlineSave(gear.id, field, e.target.value)
							}
							onKeyDown={(e) => {
								if (e.key === "Enter" && !e.shiftKey) {
									e.preventDefault();
									handleInlineSave(
										gear.id,
										field,
										e.target.value
									);
								} else if (e.key === "Escape") {
									setEditingCell(null);
								}
							}}
							className="gear-table__editable-name"
							rows={3}
						/>
					) : (
						<input
							type={type}
							defaultValue={value}
							autoFocus
							onBlur={(e) =>
								handleInlineSave(gear.id, field, e.target.value)
							}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									handleInlineSave(
										gear.id,
										field,
										e.target.value
									);
								} else if (e.key === "Escape") {
									setEditingCell(null);
								}
							}}
							className="gear-table__editable-name"
						/>
					)}
				</div>
			);
		}

		return (
			<div
				className={`gear-table__cell ${className}`}
				onClick={() => setEditingCell({ gearId: gear.id, field })}
				tabIndex={isAdmin ? 0 : -1}
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						setEditingCell({ gearId: gear.id, field });
					}
				}}
			>
				<div style={{ whiteSpace: "pre-wrap" }}>
					{value || <i className="placeholder">Click to edit</i>}
				</div>
			</div>
		);
	}

	function renderPopupMenu(gear, field, options, className = "") {
		const isEditing =
			editingCell?.gearId === gear.id && editingCell?.field === field;

		if (!isEditing) {
			const selectedOption = options.find(
				(opt) => opt.id === gear[field]
			);

			return (
				<div
					className={`gear-table__cell ${className}`}
					onClick={() => setEditingCell({ gearId: gear.id, field })}
					tabIndex={userRole === "admin" ? 0 : -1}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							setEditingCell({ gearId: gear.id, field });
						}
					}}
				>
					{selectedOption ? (
						<span
							style={{
								backgroundColor:
									selectedOption.bg_color || "transparent",
								color: selectedOption.text_color || "inherit",
							}}
						>
							<span>{selectedOption.label}</span>
						</span>
					) : (
						<i style={{ color: "#888" }}>Select</i>
					)}
				</div>
			);
		}

		return (
			<div className={`gear-table__cell status-popup`}>
				<div ref={popupRef}>
					{options.map((opt) => (
						<div
							key={opt.id}
							onClick={() => {
								handleInlineSave(gear.id, field, opt.id);
							}}
							onMouseDown={(e) => e.preventDefault()}
							style={{
								cursor: "pointer",
								display: "flex",
								alignItems: "stretch",
								justifyContent: "stretch",
								gap: "0.5em",
							}}
						>
							<span
								style={{
									backgroundColor:
										opt.bg_color || "transparent",
									color: opt.text_color || "inherit",
									borderRadius: "4px",
								}}
							>
								{opt.label}
							</span>
						</div>
					))}
				</div>
			</div>
		);
	}

	async function handleRenameGroup(oldName, newName) {
		if (!newName || oldName === newName) return;

		// Update all gears with the old group name
		const gearsToUpdate = gearList.filter(
			(g) =>
				g.group_name === oldName && g.name !== "__group_placeholder__"
		);

		try {
			await Promise.all(
				gearsToUpdate.map((gear) =>
					updateGearField(gear.id, "group_name", newName)
				)
			);

			// Also update the placeholder if it exists
			const placeholder = gearList.find(
				(g) =>
					g.group_name === oldName &&
					g.name === "__group_placeholder__"
			);
			if (placeholder) {
				await updateGearField(placeholder.id, "group_name", newName);
			}

			// Update state
			setGearList((prev) =>
				prev.map((g) =>
					g.group_name === oldName ? { ...g, group_name: newName } : g
				)
			);
		} catch (err) {
			alert("Failed to rename group: " + err.message);
		}
	}

	async function handleDeleteGroup(groupName) {
		const confirmed = window.confirm(
			"Are you sure you want to delete this group? If you do, all items will still remain in the case."
		);
		if (!confirmed) return;

		const gearsToUpdate = gearList.filter(
			(g) => g.group_name === groupName
		);

		try {
			// Remove placeholder from DB
			const placeholder = gearsToUpdate.find(
				(g) => g.name === "__group_placeholder__"
			);
			if (placeholder) {
				await removeGear(placeholder.id);
			}

			// Ungroup other items
			await Promise.all(
				gearsToUpdate
					.filter((g) => g.name !== "__group_placeholder__")
					.map((g) => updateGearField(g.id, "group_name", null))
			);

			// Update gear list state
			setGearList((prev) =>
				prev
					.filter(
						(g) =>
							!(
								g.group_name === groupName &&
								g.name === "__group_placeholder__"
							)
					)
					.map((g) =>
						g.group_name === groupName
							? { ...g, group_name: null }
							: g
					)
			);

			// ✅ Remove stale group input from state
			setNewGearByGroup((prev) => {
				const copy = { ...prev };
				delete copy[groupName];
				return copy;
			});
		} catch (err) {
			alert("Failed to delete group: " + err.message);
		}
	}

	// Handle drag end with cross-group support, update order and group_name in DB and local state
	const handleDragEnd = async (result) => {
		if (userRole !== "admin") return; // 🔒 ignore DnD for non-admins
		if (!result.destination) return;

		const { source, destination } = result;
		const sourceGroup = source.droppableId;
		const destGroup = destination.droppableId;

		// Get items list for source and destination groups
		const getItemsByGroup = (group) => {
			if (group === "Singles") {
				return [...ungroupedGear];
			}
			return [
				...(groupedGear.find((g) => g.groupName === group)?.items ||
					[]),
			];
		};

		// Clone both source and destination lists
		const sourceItems = getItemsByGroup(sourceGroup);
		const destItems =
			sourceGroup === destGroup
				? sourceItems
				: getItemsByGroup(destGroup);

		// Moving within same group
		if (sourceGroup === destGroup) {
			const [moved] = sourceItems.splice(source.index, 1);
			sourceItems.splice(destination.index, 0, moved);

			const reordered = sourceItems.map((item, idx) => ({
				...item,
				order: idx,
				group_name: sourceGroup === "Singles" ? null : sourceGroup,
			}));

			// Update local gear list state
			setGearList((prev) =>
				prev.map((gear) => {
					const updated = reordered.find((g) => g.id === gear.id);
					return updated
						? {
								...gear,
								order: updated.order,
								group_name: updated.group_name,
						  }
						: gear;
				})
			);

			// Persist updates to DB
			try {
				await Promise.all(
					reordered.map(async (gear) => {
						const { error } = await supabase
							.from("gear")
							.update({
								order: gear.order,
								group_name: gear.group_name,
							})
							.eq("id", gear.id);
						if (error) throw new Error(error.message);
					})
				);
			} catch (err) {
				alert("Failed to save new order: " + err.message);
			}

			return;
		}

		// Moving between groups (cross-group drag)
		const [moved] = sourceItems.splice(source.index, 1);
		destItems.splice(destination.index, 0, moved);

		// Reorder source group items
		const reorderedSource = sourceItems.map((item, idx) => ({
			...item,
			order: idx,
			group_name: sourceGroup === "Singles" ? null : sourceGroup,
		}));

		// Reorder destination group items
		const reorderedDest = destItems.map((item, idx) => ({
			...item,
			order: idx,
			group_name: destGroup === "Singles" ? null : destGroup,
		}));

		// Merge all updated gear items for update and state
		const updatedItems = [...reorderedSource, ...reorderedDest];

		// Update local gear list state
		setGearList((prev) =>
			prev.map((gear) => {
				const updated = updatedItems.find((g) => g.id === gear.id);
				return updated
					? {
							...gear,
							order: updated.order,
							group_name: updated.group_name,
					  }
					: gear;
			})
		);

		// Persist all updates to DB
		try {
			await Promise.all(
				updatedItems.map(async (gear) => {
					const { error } = await supabase
						.from("gear")
						.update({
							order: gear.order,
							group_name: gear.group_name,
						})
						.eq("id", gear.id);
					if (error) throw new Error(error.message);
				})
			);
		} catch (err) {
			alert(
				"Failed to save new order after cross-group move: " +
					err.message
			);
		}
	};

	// --- ✅ Bulk Actions ---
	async function handleBulkQuantityUpdate() {
		if (!tempQuantity || isNaN(parseInt(tempQuantity))) {
			alert("Please enter a valid number for quantity.");
			return;
		}

		const quantity = parseInt(tempQuantity);
		const toastId = toast.loading("Updating items...");
		try {
			await Promise.all(
				selectedGear.map(async (gearId) => {
					await updateGearField(gearId, "quantity", quantity);
				})
			);

			setGearList((prev) =>
				prev.map((gear) =>
					selectedGear.includes(gear.id)
						? { ...gear, quantity }
						: gear
				)
			);
			toast.success("Bulk quantity update sucessful.", { id: toastId });
			setTempQuantity(""); // ✅ Reset modal field
			setShowQuantityModal(false); // ✅ Close modal
		} catch (err) {
			toast.error("Bulk quantity update failed: " + err.message, {
				id: toastId,
			});
		}
	}

	async function handleBulkStatusUpdate() {
		if (!tempStatusId) {
			alert("Please select a status.");
			return;
		}

		const statusIdNum = parseInt(tempStatusId); // force number
		const toastId = toast.loading("Updating items...");
		try {
			await Promise.all(
				selectedGear.map(async (gearId) => {
					await updateGearField(gearId, "status_id", statusIdNum);
				})
			);

			// 🔧 Update local state
			setGearList((prev) =>
				prev.map((gear) =>
					selectedGear.includes(gear.id)
						? { ...gear, status_id: statusIdNum }
						: gear
				)
			);
			toast.success("Bulk status update sucessful.", { id: toastId });
			// ✅ Reset state and close modal
			setTempStatusId("");
			setShowStatusModal(false);
		} catch (err) {
			toast.error("Bulk status update failed: " + err.message, {
				id: toastId,
			});
		}
	}

	async function handleBulkDelete() {
		if (
			!window.confirm(
				`Are you sure you want to delete ${selectedGear.length} selected item(s)?\n\nBy clicking OK the selected item(s) will be permanently deleted.\nTHIS ACTION CANNOT BE UNDONE.`
			)
		)
			return;
		const toastId = toast.loading("Deleting items...");

		try {
			await Promise.all(selectedGear.map((gearId) => removeGear(gearId)));

			// Remove from local state
			setGearList((prev) =>
				prev.filter((gear) => !selectedGear.includes(gear.id))
			);
			toast.success("Items deleted successfully.", { id: toastId });
			// Clear selection
			setSelectedGear([]);
		} catch (err) {
			toast.error("Failed to delete items: " + err.message, {
				id: toastId,
			});
		}
	}

	const handleBulkMove = async (caseId, groupName) => {
		if (userRole !== "admin") {
			return;
		}
		if (!caseId) return;
		const toastId = toast.loading("Moving items...");
		try {
			await Promise.all(
				selectedGear.map((gearId) =>
					updateGearField(gearId, "case_id", caseId).then(() =>
						updateGearField(gearId, "group_name", groupName || null)
					)
				)
			);

			// ✅ Update local state with new case and group info
			setGearList(
				(prev) =>
					prev
						.map((gear) => {
							if (!selectedGear.includes(gear.id)) return gear;

							// Moving *within* this case: update group
							if (gear.case_id === caseId) {
								return {
									...gear,
									group_name: groupName || null,
								};
							}

							// Moving to a different case: remove from current case list
							return null;
						})
						.filter(Boolean) // Remove nulls
			);
			toast.success("Items moved successfully.", { id: toastId });
			setSelectedGear([]);
			setShowMoveModal(false);
		} catch (err) {
			toast.error("Failed to move items: " + err.message, {
				id: toastId,
			});
		}
	};

	useEffect(() => {
		function handleEscape(event) {
			if (event.key === "Escape") {
				setSelectedGear([]);
			}
		}
		window.addEventListener("keydown", handleEscape);
		return () => window.removeEventListener("keydown", handleEscape);
	}, [setSelectedGear]);

	// --- 🖼️ Final Render ---
	return (
		<DragDropContext onDragEnd={handleDragEnd}>
			<div className="gear-table__container">
				<div className="gear-table">
					{/* Ungrouped Gear with DnD */}
					<Droppable
						droppableId="Singles"
						isDropDisabled={userRole !== "admin"}
					>
						{(provided) => (
							<div
								ref={provided.innerRef}
								{...provided.droppableProps}
								style={{ minHeight: "1px" }}
							>
								<div className="gear-table__header">
									<div className="gear-table__cell checkbox-cell-wrap">
										{userRole === "admin" &&
											renderSelectAllCheckbox(
												ungroupedGear
											)}
									</div>
									<div className="gear-table__cell">Name</div>
									<div className="gear-table__cell">Qty.</div>
									<div className="gear-table__cell status-cell">
										Status
									</div>
									<div className="gear-table__cell">
										Notes
									</div>
								</div>

								{ungroupedGear.map((gear, index) => (
									<Draggable
										key={gear.id}
										draggableId={String(gear.id)}
										index={index}
										isDragDisabled={userRole !== "admin"}
									>
										{(provided) => (
											<div
												className={`gear-table__row${
													selectedGear.includes(
														gear.id
													)
														? " selected"
														: ""
												}`}
												ref={provided.innerRef}
												{...provided.draggableProps}
												{...(userRole === "admin"
													? provided.dragHandleProps
													: {})}
											>
												<div className="gear-table__cell checkbox-cell-wrap">
													{userRole === "admin" &&
														renderCheckboxCell(
															gear
														)}
												</div>
												{renderEditableCell(
													gear,
													"name",
													"text"
												)}
												{renderEditableCell(
													gear,
													"quantity",
													"number",
													"quantity-cell"
												)}
												{user ? (
													renderPopupMenu(
														gear,
														"status_id",
														gearStatuses,
														"status-cell"
													)
												) : (
													<>
														{(() => {
															const status =
																gearStatuses.find(
																	(s) =>
																		s.id ===
																		gear.status_id
																);
															return (
																<div className="gear-table__cell status-cell">
																	{status ? (
																		<span
																			style={{
																				backgroundColor:
																					status.bg_color ||
																					"transparent",
																				color:
																					status.text_color ||
																					"inherit",
																			}}
																		>
																			{
																				status.label
																			}
																		</span>
																	) : (
																		<i
																			style={{
																				color: "#888",
																			}}
																		>
																			Select
																		</i>
																	)}
																</div>
															);
														})()}
													</>
												)}

												{user ? (
													renderEditableCell(
														gear,
														"notes",
														"textarea",
														"notes-cell"
													)
												) : (
													<div className="gear-table__cell notes-cell">
														{gear.notes || ""}
													</div>
												)}
											</div>
										)}
									</Draggable>
								))}

								{provided.placeholder}

								{userRole === "admin" && (
									<div className="gear-table__row gear-table__row--new">
										<div
											className="gear-table__cell"
											style={{ flex: 1 }}
										>
											<input
												type="text"
												placeholder={`Add new item`}
												value={
													newGearByGroup["Singles"] ||
													""
												}
												onChange={(e) =>
													setNewGearByGroup(
														(prev) => ({
															...prev,
															Singles:
																e.target.value,
														})
													)
												}
												onKeyDown={(e) =>
													handleAddNewGear(
														e,
														"Singles"
													)
												}
												style={{ width: "100%" }}
											/>
										</div>
									</div>
								)}
							</div>
						)}
					</Droppable>

					{/* Grouped Gear with DnD */}
					{groupedGear.map(({ groupName, items }) => (
						<React.Fragment key={groupName}>
							<div className="gear-table__group-title">
								{editingGroup === groupName ? (
									<input
										type="text"
										value={
											renamingGroups[groupName] ??
											groupName
										}
										autoFocus
										onChange={(e) =>
											setRenamingGroups((prev) => ({
												...prev,
												[groupName]: e.target.value,
											}))
										}
										onBlur={(e) => {
											const newName =
												e.target.value.trim();
											if (
												newName &&
												newName !== groupName
											) {
												handleRenameGroup(
													groupName,
													newName
												);
											}
											setEditingGroup(null);
											setRenamingGroups((prev) => {
												const copy = { ...prev };
												delete copy[groupName];
												return copy;
											});
										}}
										onKeyDown={(e) => {
											if (e.key === "Enter")
												e.target.blur();
											else if (e.key === "Escape") {
												setEditingGroup(null);
												setRenamingGroups((prev) => {
													const copy = { ...prev };
													delete copy[groupName];
													return copy;
												});
											}
										}}
									/>
								) : (
									<div
										onClick={() => {
											if (userRole === "admin") {
												setEditingGroup(groupName);
											}
										}}
										tabIndex={0}
										onKeyDown={(e) =>
											e.key === "Enter" &&
											setEditingGroup(groupName)
										}
										title="Click to rename group"
									>
										{groupName}
									</div>
								)}

								{userRole === "admin" && (
									<button
										className="delete-group-button"
										onClick={() =>
											handleDeleteGroup(groupName)
										}
										title="Delete Group"
									>
										<i className="pi pi-times"></i>
										Delete Group
									</button>
								)}
							</div>

							<div className="gear-table__group-wrapper">
								<div className="gear-table__header">
									<div className="gear-table__cell checkbox-cell-wrap">
										{userRole === "admin" &&
											renderSelectAllCheckbox(items)}
									</div>
									<div className="gear-table__cell">Name</div>
									<div className="gear-table__cell">Qty.</div>
									<div className="gear-table__cell status-cell">
										Status
									</div>
									<div className="gear-table__cell">
										Notes
									</div>
								</div>

								<Droppable
									droppableId={groupName}
									isDropDisabled={userRole !== "admin"}
								>
									{(provided) => (
										<div
											ref={provided.innerRef}
											{...provided.droppableProps}
											style={{ minHeight: "1px" }}
										>
											{items.map((gear, index) => (
												<Draggable
													key={gear.id}
													draggableId={String(
														gear.id
													)}
													index={index}
													isDragDisabled={
														userRole !== "admin"
													}
												>
													{(provided) => (
														<div
															ref={
																provided.innerRef
															}
															{...provided.draggableProps}
															{...(userRole ===
															"admin"
																? provided.dragHandleProps
																: {})}
															className={`gear-table__row${
																selectedGear.includes(
																	gear.id
																)
																	? " selected"
																	: ""
															}`}
														>
															<div className="gear-table__cell checkbox-cell-wrap">
																{userRole ===
																	"admin" &&
																	renderCheckboxCell(
																		gear
																	)}
															</div>
															{renderEditableCell(
																gear,
																"name",
																"text"
															)}
															{renderEditableCell(
																gear,
																"quantity",
																"number",
																"quantity-cell"
															)}
															{user ? (
																renderPopupMenu(
																	gear,
																	"status_id",
																	gearStatuses,
																	"status-cell"
																)
															) : (
																<>
																	{(() => {
																		const status =
																			gearStatuses.find(
																				(
																					s
																				) =>
																					s.id ===
																					gear.status_id
																			);
																		return (
																			<div className="gear-table__cell status-cell">
																				{status ? (
																					<span
																						style={{
																							backgroundColor:
																								status.bg_color ||
																								"transparent",
																							color:
																								status.text_color ||
																								"inherit",
																						}}
																					>
																						{
																							status.label
																						}
																					</span>
																				) : (
																					<i
																						style={{
																							color: "#888",
																						}}
																					>
																						Select
																					</i>
																				)}
																			</div>
																		);
																	})()}
																</>
															)}

															{user ? (
																renderEditableCell(
																	gear,
																	"notes",
																	"textarea",
																	"notes-cell"
																)
															) : (
																<div className="gear-table__cell notes-cell">
																	{gear.notes ||
																		""}
																</div>
															)}
														</div>
													)}
												</Draggable>
											))}
											{provided.placeholder}
										</div>
									)}
								</Droppable>

								{userRole === "admin" && (
									<div className="gear-table__row gear-table__row--new">
										<div
											className="gear-table__cell"
											style={{ flex: 1 }}
										>
											<input
												type="text"
												placeholder={`Add new item to "${groupName}"`}
												value={
													newGearByGroup[groupName] ||
													""
												}
												onChange={(e) =>
													setNewGearByGroup(
														(prev) => ({
															...prev,
															[groupName]:
																e.target.value,
														})
													)
												}
												onKeyDown={(e) =>
													handleAddNewGear(
														e,
														groupName
													)
												}
												style={{ width: "100%" }}
											/>
										</div>
									</div>
								)}
							</div>
						</React.Fragment>
					))}

					{userRole === "admin" && (
						<div className="gear-table__add-new-group">
							<input
								type="text"
								placeholder="Add new group"
								value={newGroupName}
								onChange={(e) =>
									setNewGroupName(e.target.value)
								}
								onKeyDown={handleAddNewGroup}
							/>
						</div>
					)}

					{/* ✅ Bulk action bar */}
					{userRole === "admin" && (
						<div
							className={`bulk-actions-bar ${
								selectedGear.length > 0
									? "bulk-actions-bar--visible"
									: ""
							}`}
						>
							<span className="selected-text">
								{selectedGear.length} case
								{selectedGear.length > 1 && <>s</>} selected
							</span>
							<div
								style={{
									display: "flex",
									gap: "0.5em",
									marginLeft: "auto",
									justifyContent: "flex-end",
								}}
							>
								<div className="bulk-actions-bar__box">
									<button
										onClick={() => setShowMoveModal(true)}
									>
										<i className="pi pi-file-export"></i>
										Move
									</button>
								</div>

								<div className="bulk-actions-bar__box">
									<button
										onClick={() =>
											setShowQuantityModal(true)
										}
									>
										<i className="pi pi-tags"></i>
										Quantity
									</button>
								</div>

								<div className="bulk-actions-bar__box">
									<button
										onClick={() => setShowStatusModal(true)}
									>
										<i className="pi pi-question-circle"></i>
										Status
									</button>
								</div>
								<div className="bulk-actions-bar__box">
									<button
										onClick={handleBulkDelete}
										className="bulk-delete-button"
									>
										<i className="pi pi-trash"></i>
										Delete
									</button>
								</div>
							</div>
						</div>
					)}
					{/* Modal for Bulk Move */}
					{showMoveModal && (
						<div className="modal-overlay">
							<div className="modal-content" ref={modalRef}>
								<h3>Select Destination</h3>
								<p className="modal__current-case">
									Current Case: <span>{caseName}</span>
								</p>
								<div className="pill-wrapper">
									{associatedKits.length > 0 &&
										associatedKits.map((kit) => (
											<div
												key={kit.id}
												value={kit.id}
												className="pill"
											>
												{kit.color && (
													<span
														style={{
															backgroundColor:
																kit.color,
														}}
														className="color"
													></span>
												)}
												{kit.name}
											</div>
										))}
								</div>
								<div className="move-modal__list">
									{moveOptions.map((kit) => {
										const isExpanded =
											expandedKits.includes(kit.id);
										return (
											<div
												key={kit.id}
												className="list-item"
											>
												<div className="move-modal-stack">
													<h4
														onClick={() =>
															toggleKit(kit.id)
														}
														className="move-modal__kit-name"
													>
														{isExpanded ? (
															<i className="pi pi-chevron-down kit-toggle"></i>
														) : (
															<i className="pi pi-chevron-right kit-toggle"></i>
														)}
														{kit.name}
													</h4>

													{isExpanded && (
														<ul>
															{kit.cases.map(
																(c) => (
																	<React.Fragment
																		key={`${kit.id}-${c.id}`}
																	>
																		<li
																			onClick={() =>
																				handleBulkMove(
																					c.id,
																					null
																				)
																			}
																			className="move-modal__case"
																		>
																			{
																				c.name
																			}
																		</li>
																		{c.groups.map(
																			(
																				group
																			) => (
																				<li
																					key={`${c.id}-${group}`}
																					onClick={() =>
																						handleBulkMove(
																							c.id,
																							group
																						)
																					}
																					className="move-modal__group"
																				>
																					{
																						c.name
																					}{" "}
																					&gt;{" "}
																					{
																						group
																					}
																				</li>
																			)
																		)}
																	</React.Fragment>
																)
															)}
														</ul>
													)}
												</div>
											</div>
										);
									})}
								</div>
								<div className="modal-footer">
									<button
										onClick={() => setShowMoveModal(false)}
										className="button"
									>
										Cancel
									</button>
								</div>
							</div>
						</div>
					)}

					{showQuantityModal && (
						<div
							className="modal-overlay quantity-modal"
							onClick={() => setShowQuantityModal(false)}
						>
							<div
								className="modal-content"
								onClick={(e) => e.stopPropagation()}
							>
								<h3>Set Quantity</h3>
								<input
									type="number"
									placeholder="Set quantity"
									value={tempQuantity}
									onChange={(e) =>
										setTempQuantity(e.target.value)
									}
									className="quantity-modal__input"
								/>

								<div className="quantity-modal__actions">
									<button
										onClick={() =>
											setShowQuantityModal(false)
										}
										className="button"
									>
										Cancel
									</button>
									<button
										onClick={() => {
											//setBulkQuantity(tempQuantity);
											handleBulkQuantityUpdate();
											setShowQuantityModal(false);
											setTempQuantity("");
										}}
										disabled={tempQuantity === ""}
										className="button green"
									>
										Apply
									</button>
								</div>
							</div>
						</div>
					)}
					{showStatusModal && (
						<div
							className="modal-overlay status-modal"
							onClick={() => setShowStatusModal(false)}
						>
							<div
								className="modal-content"
								onClick={(e) => e.stopPropagation()}
							>
								<h3>Select Status</h3>
								<select
									value={tempStatusId}
									onChange={(e) =>
										setTempStatusId(e.target.value)
									}
									className="status-modal__select"
								>
									<option value="">Select status</option>
									{gearStatuses.map((status) => (
										<option
											key={status.id}
											value={status.id}
										>
											{status.label}
										</option>
									))}
								</select>

								<div className="status-modal__actions">
									<button
										onClick={() =>
											setShowStatusModal(false)
										}
										className="button"
									>
										Cancel
									</button>
									<button
										onClick={() => {
											//setBulkStatusId(tempStatusId);
											handleBulkStatusUpdate();
											setShowStatusModal(false);
											setTempStatusId("");
										}}
										disabled={tempStatusId === ""}
										className="button green"
									>
										Apply
									</button>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</DragDropContext>
	);
}
